import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faAws, 
  faGoogle, 
  faGithub 
} from '@fortawesome/free-brands-svg-icons';
import {
  faChevronDown,
  faChevronRight,
  faDollarSign,
  faServer,
  faDatabase,
  faCloud
} from '@fortawesome/free-solid-svg-icons';
import { CostData } from '../types';

interface CloudBreakdownChartProps {
  data: CostData | null;
  loading?: boolean;
  error?: string | null;
}

interface CloudSectionProps {
  title: string;
  icon: any;
  totalCost: number;
  percentage: number;
  color: string;
  accounts: any[];
  services: any[];
  expanded: boolean;
  onToggle: () => void;
  loading?: boolean;
}

const CloudSection: React.FC<CloudSectionProps> = ({
  title,
  icon,
  totalCost,
  percentage,
  color,
  accounts,
  services,
  expanded,
  onToggle,
  loading = false
}) => {
  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-2 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`w-6 h-6 bg-gray-200 rounded`}></div>
            <div>
              <div className="h-3 bg-gray-200 rounded w-12 mb-0.5"></div>
              <div className="h-2 bg-gray-200 rounded w-10"></div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="h-3 bg-gray-200 rounded w-16"></div>
            <div className="h-3 bg-gray-200 rounded w-10"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-300 rounded">
      <div
        className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 ${color} rounded flex items-center justify-center`}>
              <FontAwesomeIcon icon={icon} className="text-white text-sm" />
            </div>
            <div>
              <div className="text-sm font-bold text-gray-900">{title}</div>
              <div className="text-xs text-gray-500">
                {accounts.length} account{accounts.length !== 1 ? 's' : ''} • {services.length} services
              </div>
            </div>
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              className="text-gray-400 text-xs ml-2"
            />
          </div>
          <div className="flex items-center space-x-3 text-right">
            <div>
              <div className="text-lg font-bold text-gray-900">
                ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-gray-500">
                {percentage.toFixed(1)}% of total
              </div>
            </div>
            <div className="w-20 bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${color}`}
                style={{ width: `${Math.min(percentage, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-300 p-3 bg-gray-50">
          {/* Accounts/Projects Section */}
          <div className="mb-3">
            <h4 className="text-xs font-bold text-gray-700 mb-2">
              {title === 'Amazon Web Services' ? 'Accounts' :
               title === 'Google Cloud Platform' ? 'Projects' : 'Organizations'}
            </h4>
            <div className="space-y-2">
              {accounts.map((account, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {account.accountName || account.projectName || account.orgName}
                  </span>
                  <span className="font-bold text-gray-900">
                    ${account.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
              {accounts.length === 0 && (
                <div className="text-xs text-gray-500 italic">
                  No connected accounts
                </div>
              )}
            </div>
          </div>

          {/* Services Section */}
          <div>
            <h4 className="text-xs font-bold text-gray-700 mb-2">Top Services</h4>
            <div className="space-y-2">
              {services.map((service, index) => {
                const serviceIcon =
                  service.serviceName === 'EC2' || service.serviceName === 'Compute Engine' ? faServer :
                  service.serviceName === 'S3' || service.serviceName === 'Cloud Storage' ? faDatabase :
                  service.serviceName === 'RDS' || service.serviceName === 'BigQuery' ? faDatabase :
                  faCloud;

                return (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <FontAwesomeIcon icon={serviceIcon} className="text-gray-400 text-sm" />
                      <span className="text-gray-600">{service.serviceName}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-gray-900">
                        ${service.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({service.percentage.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const CloudBreakdownChart: React.FC<CloudBreakdownChartProps> = ({ 
  data, 
  loading = false,
  error = null
}) => {
  const [expandedClouds, setExpandedClouds] = useState<{ [key: string]: boolean }>({});

  const toggleCloudExpansion = (cloudName: string) => {
    setExpandedClouds(prev => ({
      ...prev,
      [cloudName]: !prev[cloudName]
    }));
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-2">
        <h3 className="text-xs font-semibold text-gray-900 mb-2">Cloud Provider Breakdown</h3>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Handle error state or no data
  if (error || !data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-3 text-center">
        <FontAwesomeIcon icon={faCloud} className="text-3xl text-gray-400 mb-2" />
        <h3 className="text-xs font-semibold text-gray-900 mb-1">Cloud Provider Breakdown</h3>
        <p className="text-[10px] text-gray-600">
          {error ? 'Unable to load cloud provider data.' : 'No cloud provider data available.'}
        </p>
      </div>
    );
  }

  const totalSpend = data.summary.totalSpend;
  
  // Handle the case where clouds data might not be available or have different structure
  const clouds = data.clouds || {};
  const awsData = clouds.aws || { totalSpend: 0, accounts: [], services: [] };
  const gcpData = clouds.gcp || { totalSpend: 0, projects: [], services: [] };
  const githubData = clouds.github || { totalSpend: 0, orgs: [], usage: [] };

  const cloudData = [
    {
      name: 'aws',
      title: 'Amazon Web Services',
      icon: faAws,
      color: 'bg-orange-400',
      totalCost: awsData.totalSpend,
      percentage: totalSpend > 0 ? (awsData.totalSpend / totalSpend) * 100 : 0,
      accounts: awsData.accounts || [],
      services: awsData.services || []
    },
    {
      name: 'gcp',
      title: 'Google Cloud Platform',
      icon: faGoogle,
      color: 'bg-blue-400',
      totalCost: gcpData.totalSpend,
      percentage: totalSpend > 0 ? (gcpData.totalSpend / totalSpend) * 100 : 0,
      accounts: gcpData.projects || [],
      services: gcpData.services || []
    },
    {
      name: 'github',
      title: 'GitHub',
      icon: faGithub,
      color: 'bg-gray-700',
      totalCost: githubData.totalSpend,
      percentage: totalSpend > 0 ? (githubData.totalSpend / totalSpend) * 100 : 0,
      accounts: githubData.orgs || [],
      services: (githubData.usage || []).map(u => ({ 
        serviceName: u.type ? u.type.charAt(0).toUpperCase() + u.type.slice(1) : 'Unknown', 
        totalCost: u.cost || 0, 
        percentage: githubData.totalSpend > 0 ? ((u.cost || 0) / githubData.totalSpend) * 100 : 0 
      }))
    }
  ].sort((a, b) => b.totalCost - a.totalCost);

  return (
    <div className="space-y-3">
      {/* Cloud Provider Sections */}
      <div className="space-y-3">
        {loading ? (
          // Loading skeleton
          [1, 2, 3].map((i) => (
            <CloudSection
              key={i}
              title=""
              icon={faDollarSign}
              totalCost={0}
              percentage={0}
              color="bg-gray-200"
              accounts={[]}
              services={[]}
              expanded={false}
              onToggle={() => {}}
              loading={true}
            />
          ))
        ) : (
          cloudData.map((cloud) => (
            <CloudSection
              key={cloud.name}
              title={cloud.title}
              icon={cloud.icon}
              totalCost={cloud.totalCost}
              percentage={cloud.percentage}
              color={cloud.color}
              accounts={cloud.accounts}
              services={cloud.services}
              expanded={expandedClouds[cloud.name] || false}
              onToggle={() => toggleCloudExpansion(cloud.name)}
            />
          ))
        )}
      </div>

      {/* Summary Chart */}
      {!loading && (
        <div className="bg-white border border-gray-300 rounded p-3">
          <h4 className="text-sm font-bold text-gray-900 mb-3">Cost Distribution</h4>
          <div className="space-y-3">
            {cloudData.map((cloud) => (
              <div key={cloud.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 ${cloud.color} rounded`}></div>
                  <span className="text-sm text-gray-700">{cloud.title}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-bold text-gray-900">
                    ${cloud.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-xs text-gray-500 w-12 text-right">
                    {cloud.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Visual bar chart */}
          <div className="mt-3 space-y-3">
            {cloudData.map((cloud) => (
              <div key={cloud.name} className="flex items-center space-x-3">
                <div className="w-16 text-xs text-gray-600">{cloud.title.split(' ')[0]}</div>
                <div className="flex-1 bg-gray-200 rounded-full h-2.5 relative overflow-hidden">
                  <div
                    className={`h-2.5 rounded-full ${cloud.color} transition-all duration-500`}
                    style={{ width: `${Math.min(cloud.percentage, 100)}%` }}
                  ></div>
                </div>
                <div className="w-12 text-xs text-right text-gray-500">
                  {cloud.percentage.toFixed(1)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};